import React, { useEffect, useState } from "react";
import { Edit2, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import CourseForm from "./CourseForm";
import {
  createCourse,
  deleteCourse,
  getCourses,
} from "../../services/CourseService";
import { Tooltip } from "@mui/material";
import DeleteConfirmModal from "../common/DeleteConfirmModal";

const configDate = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZone: "Asia/Ho_Chi_Minh",
};

export default function Courses() {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: 0,
    type: "",
    examIDs: [],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 6;
  const [isEditing, setIsEditing] = useState(null);
  const [isSearch, setIsSearch] = useState(false);
  const [listCourses, setListCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    courseId: null,
    courseTitle: "",
  });

  const handleFetch = async () => {
    try {
      const response = await getCourses(currentPage, limit, searchQuery);
      setListCourses(response?.data || []);
      setTotalPages(response?.totalPages || 1);
      setCurrentPage(response?.currentPage || 1);
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Lỗi khi tải danh sách khóa học");
    }
  };

  useEffect(() => {
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleEditCourse = (course) => {
    setIsEditing(true);
    setFormData({
      _id: course._id,
      title: course.title || "",
      description: course.description || "",
      price: course.price || 0,
      type: course.type || "",
      examIDs: Array.isArray(course.examIDs) ? course.examIDs : [],
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteCourse = (id, title) => {
    setDeleteModal({
      isOpen: true,
      courseId: id,
      courseTitle: title || "khóa học này",
    });
  };

  const confirmDeleteCourse = async () => {
    try {
      const res = await deleteCourse(deleteModal.courseId);
      toast.success(res?.message || "Xóa khóa học thành công");
      setListCourses(
        listCourses.filter((course) => course?._id !== deleteModal.courseId)
      );
      setDeleteModal({ isOpen: false, courseId: null, courseTitle: "" });
      handleFetch();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Lỗi khi xóa khóa học");
      setDeleteModal({ isOpen: false, courseId: null, courseTitle: "" });
    }
  };

  const handleInsertCourse = async () => {
    if (validateForm()) {
      try {
        const res = await createCourse(formData);
        if (res && res.data) {
          setListCourses([res.data, ...listCourses]);
          handleFetch();
          toast.success(res?.message || "Tạo khóa học thành công");
          setFormData({
            title: "",
            description: "",
            price: 0,
            type: "",
            examIDs: [],
          });
          setIsEditing(null);
          // Scroll to form
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message || "Lỗi khi tạo khóa học");
      }
    }
  };

  const handleUpdateCourse = async () => {
    if (validateForm()) {
      try {
        const res = await createCourse(formData);
        if (res && res.data) {
          setListCourses(
            listCourses.map((e) => (e._id === res.data?._id ? res.data : e))
          );
          handleFetch();
          toast.success(res?.message || "Cập nhật khóa học thành công");
          setFormData({
            title: "",
            description: "",
            price: 0,
            type: "",
            examIDs: [],
          });
          setIsEditing(null);
          // Scroll to form
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message || "Lỗi khi cập nhật khóa học");
      }
    }
  };

  const validateForm = () => {
    if (!formData.title) {
      toast.error("Vui lòng nhập tiêu đề");
      return false;
    }

    if (!formData.description) {
      toast.error("Vui lòng nhập mô tả");
      return false;
    }

    if (formData.price === null || formData.price === undefined) {
      toast.error("Vui lòng nhập giá");
      return false;
    }

    if (formData.price < 0) {
      toast.error("Giá không được âm");
      return false;
    }

    if (!formData.type) {
      toast.error("Vui lòng chọn loại khóa học");
      return false;
    }

    return true;
  };

  const handleChangeInputCourse = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: name === "price" ? parseFloat(value) || 0 : value,
    });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    if (searchQuery) {
      setIsSearch(true);
    } else {
      setIsSearch(false);
    }
    setCurrentPage(1);
    handleFetch();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (searchQuery) {
        setIsSearch(true);
      } else {
        setIsSearch(false);
      }
      handleSearch();
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price || 0);
  };

  return (
    <div className="p-[30px] overflow-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">
        Quản Lý Khóa Học
      </h2>

      {/* Course Form */}
      <CourseForm
        isEditing={!!isEditing}
        formData={formData}
        handleInsertCourse={handleInsertCourse}
        handleChangeInputCourse={handleChangeInputCourse}
        handleUpdateCourse={handleUpdateCourse}
      />

      {/* Search Section */}
      <div className="mb-4 flex items-center space-x-2 flex-wrap gap-4">
        <input
          type="text"
          placeholder="Tìm kiếm khóa học..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          className="w-96 px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring"
        />
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
        >
          Search
        </button>
      </div>

      {/* Course Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="p-3 text-left">No</th>
                <th className="p-3 text-left">Tiêu đề</th>
                <th className="p-3 text-left">Mô tả</th>
                <th className="p-3 text-left">Giá</th>
                <th className="p-3 text-left">Loại</th>
                {/* <th className="p-3 text-left">Số đề thi</th> */}
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listCourses.length > 0 ? (
                listCourses.map((course, index) => (
                  <tr
                    key={course?._id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <Tooltip title={course?._id} placement="top">
                      <td className="p-3">
                        {course?._id?.slice(0, 5)}...
                        {course?._id?.slice(-5)}
                      </td>
                    </Tooltip>
                    <td className="p-3">
                      <div className="max-w-xs truncate" title={course?.title}>
                        {course?.title}
                      </div>
                    </td>
                    <td className="p-3">
                      <div
                        className="max-w-xs truncate"
                        title={course?.description}
                      >
                        {course?.description}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="text-sm font-semibold text-green-600">
                        {formatPrice(course?.price)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {course?.type || "N/A"}
                      </span>
                    </td>
                    {/* <td className="p-3">
                      <span className="text-sm text-gray-700">
                        {Array.isArray(course?.examIDs)
                          ? course.examIDs.length
                          : 0}
                      </span>
                    </td> */}
                    <td className="p-3">
                      <div className="flex items-center justify-center space-x-2 h-full min-h-[40px]">
                        <button
                          onClick={() => handleEditCourse(course)}
                          className="text-blue-500 hover:text-blue-700 transition"
                          title="Edit Course"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteCourse(course?._id, course?.title)
                          }
                          className="text-red-500 hover:text-red-700 transition"
                          title="Delete Course"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-gray-500">
                    Không có khóa học nào
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between p-4 items-center">
          <span>
            Page {isSearch ? "1 / 1" : `${currentPage} / ${totalPages}`}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1 || isSearch}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((p) => (p < totalPages ? p + 1 : p))
              }
              disabled={currentPage === totalPages || isSearch}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, courseId: null, courseTitle: "" })
        }
        onConfirm={confirmDeleteCourse}
        title="Xác nhận xóa khóa học"
        message="Bạn có chắc chắn muốn xóa khóa học này?"
        itemName={deleteModal.courseTitle}
      />
    </div>
  );
}
